import React from 'react';

function DealDetailPanel({
  deal, dealEmails, dealActivities, aiSummary, aiLoading,
  formatCurrency, formatDate, formatDateTime,
  onClose, onMarkWon, onMarkLost, onEmail, onEdit, onDelete,
  onToggleEmailTracking, onFetchEmailDetails, onGenerateAi, onToggleActionItem,
  onAddActivity
}) {
  if (!deal) return null;

  return (
    <div className="deal-detail-panel">
      <div className="panel-header" style={{ backgroundColor: deal.stage?.color || '#3b82f6' }}>
        <div>
          <h3>{deal.name}</h3>
          <span className="panel-stage">{deal.stage?.name}</span>
        </div>
        <button className="btn-close" onClick={onClose}>×</button>
      </div>

      <div className="panel-content">
        <div className="panel-actions">
          <button className="btn btn-small btn-success" onClick={onMarkWon}>Vunnen</button>
          <button className="btn btn-small btn-danger" onClick={onMarkLost}>Förlorad</button>
          <button className="btn btn-small btn-primary" onClick={onEmail}>Skicka mejl</button>
          <button className="btn btn-small btn-secondary" onClick={() => onEdit(deal)}>Redigera</button>
          <button className="btn btn-small btn-danger" onClick={() => onDelete(deal.id)}>Ta bort</button>
        </div>

        <section className="detail-section">
          <h4>Detaljer</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Värde</label>
              <span className="value-large">{formatCurrency(deal.expectedValue)}</span>
            </div>
            <div className="detail-item">
              <label>Sannolikhet</label>
              <span>{deal.probability}%</span>
            </div>
            <div className="detail-item">
              <label>Viktat värde</label>
              <span>{formatCurrency(deal.weightedValue)}</span>
            </div>
            {deal.expectedCloseDate && (
              <div className="detail-item">
                <label>Förväntad avslut</label>
                <span>{formatDate(deal.expectedCloseDate)}</span>
              </div>
            )}
            {deal.company && (
              <div className="detail-item">
                <label>Företag</label>
                <span>{deal.company.name}</span>
              </div>
            )}
            {deal.contact && (
              <div className="detail-item">
                <label>Kontakt</label>
                <span>{deal.contact.firstName} {deal.contact.lastName}</span>
              </div>
            )}
            {deal.estimatedParticipants && (
              <div className="detail-item">
                <label>Antal deltagare</label>
                <span>{deal.estimatedParticipants}</span>
              </div>
            )}
            {deal.preferredLocation && (
              <div className="detail-item">
                <label>Plats</label>
                <span>{deal.preferredLocation}</span>
              </div>
            )}
          </div>
        </section>

        {deal.customCourseDescription && (
          <section className="detail-section">
            <h4>Kursbeskrivning</h4>
            <p>{deal.customCourseDescription}</p>
          </section>
        )}

        {deal.notes && (
          <section className="detail-section">
            <h4>Anteckningar</h4>
            <p>{deal.notes}</p>
          </section>
        )}

        {/* E-posthistorik */}
        <section className="detail-section">
          <div className="section-header">
            <h4>📧 E-posthistorik ({dealEmails.length})</h4>
            <label className="email-tracking-toggle" title={deal.emailTrackingDisabled ? 'Spårning avstängd' : 'Spårning aktiv'}>
              <input
                type="checkbox"
                checked={!deal.emailTrackingDisabled}
                onChange={(e) => onToggleEmailTracking(deal.id, e.target.checked)}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">
                {deal.emailTrackingDisabled ? '🔇' : '🔔'}
              </span>
            </label>
          </div>
          {deal.emailTrackingDisabled && (
            <p className="tracking-disabled-notice">
              ⚠️ Mejlspårning avstängd - nya mejl kopplas inte automatiskt till denna affär
            </p>
          )}
          <div className="email-list">
            {dealEmails.map(email => (
              <div
                key={email.id}
                className={`email-item ${email.direction}`}
                onClick={() => onFetchEmailDetails(email.id)}
              >
                <div className="email-direction-icon">
                  {email.direction === 'inbound' ? '⬇️' : '⬆️'}
                </div>
                <div className="email-content">
                  <div className="email-header">
                    <span className="email-sender">
                      {email.direction === 'inbound'
                        ? email.senderName || email.senderEmail
                        : `Till: ${email.toRecipients?.[0]?.name || email.toRecipients?.[0]?.email || 'Okänd'}`
                      }
                    </span>
                    <span className="email-date">
                      {new Date(email.receivedAt || email.sentAt).toLocaleDateString('sv-SE', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="email-subject">{email.subject || '(Inget ämne)'}</div>
                  <div className="email-preview">{email.bodyPreview?.substring(0, 100)}...</div>
                </div>
                {email.hasAttachments && <span className="email-attachment">📎</span>}
              </div>
            ))}
            {dealEmails.length === 0 && (
              <p className="empty-message">Inga e-postmeddelanden kopplade till denna affärsmöjlighet</p>
            )}
          </div>
        </section>

        {/* AI Insikter */}
        <section className="detail-section ai-section">
          <div className="section-header">
            <h4>🤖 AI Insikter</h4>
            <button
              className="btn btn-small btn-secondary"
              onClick={() => onGenerateAi(deal.id)}
              disabled={aiLoading}
            >
              {aiLoading ? 'Genererar...' : aiSummary ? 'Uppdatera' : 'Generera'}
            </button>
          </div>

          {aiLoading && (
            <div className="ai-loading">
              <span className="spinner"></span>
              <span>Analyserar mejlhistorik med AI...</span>
            </div>
          )}

          {aiSummary && !aiLoading && (
            <div className="ai-content">
              <div className="ai-summary">
                <h5>Sammanfattning</h5>
                <p>{aiSummary.summary}</p>
              </div>
              {aiSummary.actionItems && aiSummary.actionItems.length > 0 && (
                <div className="ai-action-items">
                  <h5>Att göra</h5>
                  <ul className="action-items-list">
                    {aiSummary.actionItems.map((item, index) => (
                      <li key={index} className={item.done ? 'done' : ''}>
                        <label>
                          <input
                            type="checkbox"
                            checked={item.done}
                            onChange={(e) => onToggleActionItem(deal.id, index, e.target.checked)}
                          />
                          <span className="action-text">{item.text}</span>
                        </label>
                        {item.source && <span className="action-source">{item.source}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {aiSummary.nextStep && (
                <div className="ai-next-step">
                  <h5>Rekommenderat nästa steg</h5>
                  <p className="next-step-text">{aiSummary.nextStep}</p>
                </div>
              )}
              {aiSummary.updatedAt && (
                <p className="ai-timestamp">
                  Senast uppdaterad: {new Date(aiSummary.updatedAt).toLocaleString('sv-SE')}
                </p>
              )}
            </div>
          )}

          {!aiSummary && !aiLoading && (
            <p className="empty-message">
              Klicka "Generera" för att få en AI-sammanfattning av mejlhistoriken
            </p>
          )}
        </section>

        {/* Aktiviteter */}
        <section className="detail-section">
          <div className="section-header">
            <h4>Aktiviteter ({dealActivities.length})</h4>
            <button className="btn btn-small btn-primary" onClick={onAddActivity}>
              + Lägg till
            </button>
          </div>
          <div className="activity-timeline">
            {dealActivities.map(activity => (
              <div key={activity.id} className="activity-item">
                <div className="activity-icon" style={{ backgroundColor: activity.activityType?.color || '#6b7280' }}>
                  {activity.activityType?.icon || '📝'}
                </div>
                <div className="activity-content">
                  <div className="activity-header">
                    <span className="activity-subject">{activity.subject}</span>
                    <span className="activity-date">{formatDateTime(activity.completedAt || activity.createdAt)}</span>
                  </div>
                  {activity.description && <p className="activity-description">{activity.description}</p>}
                  {activity.user && !activity.isSystemActivity && (
                    <span className="activity-user">{activity.user.firstName} {activity.user.lastName}</span>
                  )}
                </div>
              </div>
            ))}
            {dealActivities.length === 0 && (
              <p className="empty-message">Inga aktiviteter ännu</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default DealDetailPanel;
